import { Timestamp } from 'firebase/firestore';
import { Task1Type, VisualType } from './task-bank';

export type WritingTaskType = 'task1' | 'task2';

export interface BodyParagraphData {
  type: BodyParagraphType | '';
  content: string;
}

// Enhanced discriminated union for writing tasks
export interface BaseWritingTask {
  id: string;
  userId: string;
  prompt: string;
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  lastSaved: Timestamp;
  createdAt: Timestamp;
}

export interface Task1WritingTask extends BaseWritingTask {
  taskType: 'task1';
  overview: string;
  conclusion?: string; // For backward compatibility
  imageUrl?: string;
  task1Type?: Task1Type;
  visualType?: VisualType;
  task1Ideas?: unknown;
  reportPlan?: ReportPlanItem[]; // Task 1 uses report plans
  essayPlan?: never; // Task 1 doesn't use essay plans
}

export interface Task2WritingTask extends BaseWritingTask {
  taskType: 'task2';
  conclusion: string;
  overview?: never; // Task 2 doesn't use overview
  essayPlan?: EssayPlanItem[];
  imageUrl?: never; // Task 2 doesn't use images
  task1Type?: never;
  visualType?: never;
  task1Ideas?: never;
}

export type WritingTask = Task1WritingTask | Task2WritingTask;

// Enhanced type guards with better type narrowing
export function isTask1WritingTask(
  task: WritingTask
): task is Task1WritingTask {
  return task.taskType === 'task1';
}

export function isTask2WritingTask(
  task: WritingTask
): task is Task2WritingTask {
  return task.taskType === 'task2';
}

// More specific save data types
export type Task1SaveData = Pick<
  Task1WritingTask,
  | 'taskType'
  | 'prompt'
  | 'introduction'
  | 'bodyParagraphs'
  | 'overview'
  | 'conclusion'
  | 'imageUrl'
  | 'task1Type'
  | 'visualType'
  | 'task1Ideas'
  | 'reportPlan'
>;

export type Task2SaveData = Pick<
  Task2WritingTask,
  | 'taskType'
  | 'prompt'
  | 'introduction'
  | 'bodyParagraphs'
  | 'conclusion'
  | 'essayPlan'
>;

export type WritingSaveData = Task1SaveData | Task2SaveData;

// Enhanced editor props with better type safety
export interface EssayEditorProps {
  initialPrompt?: string;
  initialIntroduction?: string;
  initialBodyParagraphs?: BodyParagraphData[];
  initialConclusion?: string;
  initialEssayPlan?: EssayPlanItem[];
  initialEssayId?: string | null;
  onSuggestionSuccess?: () => void;
  suggestionClearTrigger?: number;
}

export interface EssayEditorStateProps extends EssayEditorProps {
  user: { uid: string } | null | undefined;
  userProfile: { role?: string } | null | undefined;
}

export interface StructuredEssayData {
  type: 'essay';
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  conclusion: string;
}

// Task 1 specific types with enhanced type safety
export interface ReportEditorProps {
  initialPrompt?: string;
  initialIntroduction?: string;
  initialOverview?: string;
  initialDetailParagraphs?: BodyParagraphData[];
  initialImageUrl?: string;
  initialTask1Type?: Task1Type;
  initialVisualType?: VisualType;
  initialReportPlan?: ReportPlanItem[];
  initialEssayId?: string | null;
  onSuggestionSuccess?: () => void;
  suggestionClearTrigger?: number;
}

export interface StructuredReportData {
  type: 'report';
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  overview: string;
}

// Enhanced union type with discriminant property
export type StructuredWritingData = StructuredEssayData | StructuredReportData;

// Enhanced type guards with discriminant property
export function isReportData(
  data: StructuredWritingData
): data is StructuredReportData {
  return data.type === 'report';
}

export function isEssayData(
  data: StructuredWritingData
): data is StructuredEssayData {
  return data.type === 'essay';
}

// Helper function to create structured data with proper typing
export function createStructuredEssayData(
  introduction: string,
  bodyParagraphs: BodyParagraphData[],
  conclusion: string
): StructuredEssayData {
  return {
    type: 'essay',
    introduction,
    bodyParagraphs,
    conclusion,
  };
}

export function createStructuredReportData(
  introduction: string,
  bodyParagraphs: BodyParagraphData[],
  overview: string
): StructuredReportData {
  return {
    type: 'report',
    introduction,
    bodyParagraphs,
    overview,
  };
}

// Task 1 plan item (simpler than essay plan)
export interface ReportPlanItem {
  label: string;
  content: string;
}

// Enhanced body paragraph type with stricter constraints
export type BodyParagraphType = 'one-idea' | 'two-idea' | 'counter-argument';

// Enhanced essay plan item with better type safety
export interface EssayPlanItem {
  label: string;
  type?: BodyParagraphType; // More specific than string
  content: string;
}

// Utility types for better type inference
export type WritingTaskByType<T extends WritingTaskType> = T extends 'task1'
  ? Task1WritingTask
  : T extends 'task2'
  ? Task2WritingTask
  : never;

export type SaveDataByType<T extends WritingTaskType> = T extends 'task1'
  ? Task1SaveData
  : T extends 'task2'
  ? Task2SaveData
  : never;
